<template>
  <InteractiveForm
    ref="rfPersonaResidenceForm"
    class="PersonaResidenceInteractiveForm"
    :steps="cptdFormSteps"
    :busy="busy"
    current-form="persona-residence"
    :auto-selectable-steps="autoSelectableSteps"
    :hide-next-button="hideFormInteractiveNextButton"
    @submit="submit"
  >
    <template #default="{ step, validation }">
      <ZipCodeInput
        v-if="displayInput(step, 'zip_code')"
        key="zip_code"
        v-model="personaResidence.zip_code"
        label="Sobre onde você mora, qual é o CEP?"
        autofocus
        :validation="validation"
        @input="onCepChange($event)"
      />
      <!-- ESTADO -->
      <StateRadioList
        v-if="displayInput(step, 'state')"
        id="state"
        key="state"
        v-model="personaResidence.state"
        title="Escolha o estado que você mora"
        search-placeholder="Buscar estado"
        :searchable="true"
        :validation="validation"
        :validations="{ required: true }"
        @input="autoSelectField('state', $event)"
      />
      <CitiesRadioList
        v-if="displayInput(step, 'city')"
        id="cityOptions"
        key="cityOptions"
        v-model="personaResidence.city"
        title="Escolha a cidade que você mora"
        search-placeholder="Buscar cidade"
        :searchable="true"
        :validation="validation"
        :state="personaResidence.state"
        @input="autoSelectField('city', $event)"
      />
      <DistrictInput
        v-if="displayInput(step, 'district')"
        key="district"
        v-model="personaResidence.district"
        :validation="validation"
      />
      <AddressInput
        v-if="displayInput(step, 'address')"
        key="address"
        v-model="personaResidence.address"
        label="Agora preencha o seu endereço"
        :validation="validation"
      />
      <AddressNumberInput
        v-if="displayInput(step, 'number')"
        key="number"
        v-model="personaResidence.number"
        label="Qual o número do endereço?"
        :validation="validation"
      />
      <div
        v-if="displayInput(step, 'number') && cepWasFound.status"
        key="address_preview"
        class="address-preview"
      >
        {{ personaResidence.zip_code }} - {{ personaResidence.address }} -
        {{ personaResidence.district }} - {{ personaResidence.city }} -
        {{ personaResidence.state }}.
      </div>
      <AddressComplementInput
        v-if="displayInput(step, 'complement')"
        key="complement"
        v-model="personaResidence.complement"
        label="E o complemento?"
        :validation="validation"
      />
    </template>
  </InteractiveForm>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'

import { nextTick } from 'vue'
import InteractiveForm from '@/components/forms/InteractiveForm.vue'
import StateRadioList from '@/components/forms/fields/radioInputList/StateRadioList.vue'
import CitiesRadioList from '@/components/forms/fields/radioInputList/CitiesRadioList.vue'
import AddressNumberInput from '@/components/forms/fields/input/AddressNumberInput.vue'
import AddressComplementInput from '@/components/forms/fields/input/AddressComplementInput.vue'
import AddressInput from '@/components/forms/fields/input/AddressInput.vue'
import DistrictInput from '@/components/forms/fields/input/DistrictInput.vue'
import ZipCodeInput from '@/components/forms/fields/input/ZipCodeInput.vue'
export default {
  name: 'PersonaResidenceInteractiveForm',
  components: {
    StateRadioList,
    CitiesRadioList,
    AddressNumberInput,
    AddressComplementInput,
    AddressInput,
    DistrictInput,
    ZipCodeInput,
    InteractiveForm,
  },
  props: {
    persona: { type: Object, default: () => ({}) },
    busy: { type: Boolean, default: false },
    displaySteps: { type: Array, default: () => [] },
  },
  emits: ['submit'],
  data() {
    return {
      loading: false,
      personaResidence: {
        zip_code: '',
        number: '',
        address: '',
        district: '',
        complement: '',
        state: '',
        city: '',
      },
      autoSelectableSteps: ['state', 'city'],
      lastZipCodeSearched: '',
      hideFormInteractiveNextButton: true,
    }
  },
  computed: {
    ...mapGetters({
      addressCepData: 'services/address/addressCepData',
      cepWasFound: 'services/address/cepWasFound',
    }),
    cptdFormSteps() {
      if (this.displaySteps?.length > 0) {
        return this.displaySteps
      }
      return this.steps
    },
    steps() {
      const defaultSteps = [
        'zip_code',
        'address',
        'number',
        'complement',
        'state',
        'city',
        'district',
      ]

      if (this.cepWasFound && this.cepWasFound.status) {
        const newSteps = [...defaultSteps]
        if (this.cepWasFound.has_address) {
          newSteps.splice(newSteps.indexOf('address'), 1)
        }
        if (this.cepWasFound.has_district) {
          newSteps.splice(newSteps.indexOf('district'), 1)
        }
        if (this.cepWasFound.has_city) {
          newSteps.splice(newSteps.indexOf('city'), 1)
        }
        if (this.cepWasFound.has_state) {
          newSteps.splice(newSteps.indexOf('state'), 1)
        }

        return newSteps
      }

      return defaultSteps
    },
  },
  async mounted() {
    if (this.persona?.residence) {
      this.personaResidence.zip_code = this.persona?.residence?.zip_code || ''
      this.personaResidence.address = this.persona?.residence?.address || ''
      this.personaResidence.number = this.persona?.residence?.number || ''
      this.personaResidence.city = this.persona?.residence?.city || ''
      this.personaResidence.complement =
        this.persona?.residence?.complement || ''
      this.personaResidence.district = this.persona?.residence?.district || ''
      this.personaResidence.state =
        this.persona?.residence?.state &&
        this.persona?.residence?.state !== 'UNKNOWN_STATE'
          ? this.persona?.residence?.state
          : ''
    }

    if (this.persona?.residence?.zip_code) {
      await this.searchZipCode(this.persona?.residence.zip_code)
    }
  },
  methods: {
    ...mapActions({
      actGetCep: 'services/address/actGetCep',
    }),
    async onCepChange(v) {
      this.personaResidence.zip_code = v
      if (v.length === 9) {
        await this.searchZipCode(v)
        this.hideFormInteractiveNextButton = false
      } else {
        this.hideFormInteractiveNextButton = true
      }
    },
    async searchZipCode(cep) {
      if (cep === this.lastZipCodeSearched) {
        return
      }

      this.lastZipCodeSearched = cep
      await this.actGetCep(cep || this.personaResidence?.zip_code)

      if (this.cepWasFound.status) {
        this.personaResidence.address = this.addressCepData?.address
        this.personaResidence.district = this.addressCepData?.neighborhood
        this.personaResidence.state = this.addressCepData?.state
        this.personaResidence.city = this.addressCepData?.city
      }
    },
    autoSelectField(key, value) {
      this.personaResidence[key] = value
      nextTick(() => {
        this.$refs.rfPersonaResidenceForm.next()
      })
    },
    selectField(key, value) {
      this.personaResidence[key] = value
    },
    displayInput(step, key) {
      return step === key
    },
    submit() {
      this.$emit('submit', this.personaResidence)
    },
  },
}
</script>
<style lang="scss">
.PersonaResidenceInteractiveForm {
  .address-preview {
    padding: 16px;
    background: $color-neutral-light;
    margin: 0 0 24px 0;
    @include caption;
    color: $color-black-high;
  }
}
</style>
